import React from 'react';
import PropTypes from 'prop-types';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { createLogger } from '../../../utils/logger';

const logger = createLogger('NavigationBar');

const NavigationBar = ({ 
  currentQuestionIndex, 
  totalQuestions, 
  freeNavigation, 
  sessionResponses, 
  questions,
  isTimeUp,
  quickAnswerMode = 'standard',  // 設定ベースの制御を追加
  onPrevious, 
  onNext, 
  onNavigateTo,
  onShowResults  // 結果表示処理を追加
}) => {
  const isFirstQuestion = currentQuestionIndex === 0;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const hasCurrentAnswer = sessionResponses[questions[currentQuestionIndex]?.id];
  
  // 全問回答完了チェック
  const allAnswered = questions.length > 0 && questions.every(q => sessionResponses[q.id]);
  
  const canGoToNext = freeNavigation || hasCurrentAnswer || isTimeUp;
  
  return (
    <div className="space-y-4">
      {/* 問題インジケーター */}
      {freeNavigation && (
        <div className="flex flex-wrap gap-2 justify-center">
          {questions.map((_, index) => (
            <button
              key={index}
              onClick={() => onNavigateTo(index)}
              disabled={isTimeUp}
              className={`w-8 h-8 rounded-full flex items-center justify-center
                ${currentQuestionIndex === index
                  ? 'bg-purple-600 text-white'
                  : sessionResponses[questions[index]?.id]
                    ? 'bg-purple-200 text-purple-800'
                    : 'bg-gray-200 text-gray-600'
                } ${isTimeUp ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'} transition-opacity`}
              aria-label={`問題${index + 1}へ移動${isTimeUp ? '（時間切れのため無効）' : ''}`}
            >
              {index + 1}
            </button>
          ))}
        </div>
      )}

      {/* ナビゲーションボタン */}
      <div className="flex justify-between">
        <button
          onClick={onPrevious}
          disabled={isFirstQuestion || !freeNavigation || (isTimeUp && freeNavigation)}
          className={`flex items-center gap-2 px-4 py-2 rounded
            ${(isFirstQuestion || !freeNavigation || (isTimeUp && freeNavigation))
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'text-purple-600 hover:text-purple-800'}`}
          aria-label={`前の問題に戻るボタン${(isTimeUp && freeNavigation) ? '（時間切れのため無効）' : ''}`}
        >
          <ArrowLeft size={20} />
          前の問題
        </button>
        
        {/* 結果表示ボタンの表示条件（設定ベース方式）：
            - quickAnswerMode='quick'：自動遷移のため結果ボタン非表示
            - quickAnswerMode='standard'：手動遷移のため条件に応じて表示
              * 自由移動モード：全問回答完了時
              * 一方向移動モード：最後の問題で回答済み時 */}
        {quickAnswerMode !== 'quick' && (freeNavigation ? allAnswered : (isLastQuestion && hasCurrentAnswer)) ? (
          <button
            onClick={() => {
              logger.info('結果表示ボタンクリック');
              logger.debug('クリック時の状態', {
                allAnswered,
                isLastQuestion,
                hasCurrentAnswer,
                freeNavigation
              });
              onShowResults();
            }}
            className="flex items-center gap-2 px-4 py-2 rounded bg-purple-600 text-white hover:bg-purple-700"
            aria-label="結果を表示するボタン"
          >
            結果を表示
            <ArrowRight size={20} />
          </button>
        ) : (
          <button
            onClick={onNext}
            disabled={isLastQuestion || !canGoToNext || (isTimeUp && freeNavigation)}
            className={`flex items-center gap-2 px-4 py-2 rounded
              ${(isLastQuestion || !canGoToNext || (isTimeUp && freeNavigation))
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'text-purple-600 hover:text-purple-800'}`}
            aria-label={`次の問題に進むボタン${(isTimeUp && freeNavigation) ? '（時間切れのため無効）' : ''}`}
          >
            次の問題
            <ArrowRight size={20} />
          </button>
        )}
      </div>
    </div>
  );
};

NavigationBar.propTypes = {
  currentQuestionIndex: PropTypes.number.isRequired,
  totalQuestions: PropTypes.number.isRequired,
  freeNavigation: PropTypes.bool.isRequired,
  sessionResponses: PropTypes.object.isRequired,
  questions: PropTypes.array.isRequired,
  isTimeUp: PropTypes.bool,
  quickAnswerMode: PropTypes.string,  // 設定ベース制御用プロパティ
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onNavigateTo: PropTypes.func.isRequired,
  onShowResults: PropTypes.func.isRequired,  // 必須プロパティとして追加
};

export default NavigationBar; 